import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { MAX_SEATS } from '@lobby/components/Table/components/TableInfo/constants';
import { categoriesSelector } from '@lobby/store/selectors/categories';
import {
  gameTypesSelector,
  isCustomLobbySelector,
  languageCodeSelector,
} from '@lobby/store/selectors/config';
import {
  favoriteSelector,
  recentSelector,
  tablesSelector,
} from '@lobby/store/selectors/tables';
import {
  userIdSelector, userOperatorIdSelector, userSelector, userTokenSelector,
} from '@lobby/store/selectors/user';
import { isBlackjack } from '@lobby/utils/functions';
import { getTableURL } from '@lobby/utils/getTableURL';
import { getGameNameById, isReferrerSameHostname } from '@lobby/utils/navigation';
import { combineEpics, ofType } from 'redux-observable';
import {
  concat,
  from,
  fromEvent,
  interval,
  of,
} from 'rxjs';
import {
  catchError,
  filter,
  first,
  flatMap,
  ignoreElements,
  map,
  mergeMap,
  pluck,
  switchMap,
  tap,
  timestamp,
} from 'rxjs/operators';

import { analyticsStore, sendAnalytics } from '@ezugi/analytics/sendAnalytics';
import { mq } from '@ezugi/constants/breakpoints';
import { queryParams } from '@ezugi/utils/functions/queryParams/queryParams';
import { getDiffTimeAsADouble } from '@ezugi/utils/functions/timeDiff';
import { DeviceInfo } from '@ezugi/utils/helpers/deviceInfo';
import { getAuthEndpointForExternalIntegration } from '@ezugi/utils/helpers/getAuthEndpointForExternalIntegration';
import { getFrame } from '@ezugi/utils/helpers/getFrame';
import { getOrientation, ORIENTATION } from '@ezugi/utils/helpers/getOrientation';
import isFullscreen from '@ezugi/utils/predicates/isFullscreen';

import { API_HOST } from '../../../endpoints';
import { fetchOutgoingCalls } from '../../../utils/fetchOutgoingCalls';
import { analytics } from '../../analytics';
import { app } from '../../app';
import { categories } from '../../categories';
import { config } from '../../config';
import { history } from '../../history';
import { navigation } from '../../navigation';
import { appErrorSelector } from '../../selectors/app';
import { areOutgoingCallsEnabledSelector } from '../../selectors/config';
import { externalGameTypesSelector } from '../../selectors/externalGameTypes';
import { userRequestTimestampSelector } from '../../selectors/user';
import { socket } from '../../socket';
import { tables } from '../../tables';
import { user as userSlice } from '../../user';
import {
  CHANGE_ORIENTATION,
  EXTERNAL_VISITOR,
  GAME_MENU_PLAYER_HISTORY_DAILY,
  INTERNAL_VISITOR,
  LOADING_STATUS_ERROR,
  LOADING_STATUS_INITIALIZATION,
  LOADING_STATUS_SUCCESS,
  LOBBY_CATEGORY_CLICK,
  LOBBY_FAVORITE,
  LOBBY_INITIAL_DATA,
  LOBBY_PLAY,
} from './constants';

// Set uid as GA UserId
const setUserIdEpic = (action$, state$) => action$.pipe(
  ofType(analytics.push),
  first(),
  filter(() => typeof window.ga === 'function'),
  tap(() => {
    const user = userSelector(state$.value);

    window.ga('set', 'userId', String(user.uid));
  }),
  ignoreElements(),
);

const analyticsPushEpic = (action$, state$) => action$.pipe(
  ofType(analytics.push),
  filter(() => areOutgoingCallsEnabledSelector(state$.value)),
  pluck('payload'),
  tap(({ created_at, local_time, ...payload }) => {
    const state = state$.value;
    const user = userSelector(state);
    const { operatorId, uid } = user;

    payload.operatorId = operatorId ?? String(queryParams.getQueryParams().operatorId);
    payload.playerId = uid;

    const date = new Date();
    payload.created_at = created_at || date.toUTCString();
    payload.local_time = local_time || date.toString();
    payload.custom_ezugi_timestamp = Date.now();
    payload.visitor_source = isReferrerSameHostname() ? INTERNAL_VISITOR : EXTERNAL_VISITOR;
    payload.isCustomLobby = isCustomLobbySelector(state$.value);
    payload.ezugi_session_id = user.session_id;
    payload.clientId = user.client_id;
    payload.resolution = `${window.innerWidth},${window.innerHeight}`;
    payload.pos = getOrientation();
    payload.full_screen = +isFullscreen();

    sendAnalytics(payload);
  }),
  ignoreElements(),
);

const lobbyCategoryClick = (action$) => action$.pipe(
  ofType(categories.click),
  pluck('payload'),
  map(({ categoryName }) => analytics.push({
    event: LOBBY_CATEGORY_CLICK,
    category_name: categoryName,
  })),
);

const lobbyFavorite = (action$) => action$.pipe(
  ofType(tables.setFavorite),
  pluck('payload'),
  map((payload) => analytics.push({
    event: LOBBY_FAVORITE,
    tableId: payload.table_id,
    status: payload.action === 'add',
  }),),
);

const lobbyPlay = (action$, state$) => action$.pipe(
  ofType(navigation.openTable),
  pluck('payload'),
  map(({ seatIndex, limitId, tableId }) => {
    const table = tablesSelector(state$.value)[tableId];
    const gameTypes = gameTypesSelector(state$.value);
    const gameId = table.gameId;
    const gameName = getGameNameById(gameTypes, gameId);
    const pathSegment = getAuthEndpointForExternalIntegration(
      gameId,
      externalGameTypesSelector(state$.value)
    );
    const { identifier } = userSelector(state$.value);
    const pageURL = getTableURL({
      baseURL: API_HOST,
      gameName,
      pathSegment,
      queryParams: {
        game_id: gameId,
        table_id: tableId,
        limit_id: limitId,
        identifier,
        ...(seatIndex && { seatIndex }),
        operatorId: userOperatorIdSelector(state$.value),
        ...(!!pathSegment && {
          language: languageCodeSelector(state$.value),
          token: userTokenSelector(state$.value),
          uid: userIdSelector(state$.value),
          openTable: tableId,
          fromEzugiLobby: true,
        }),
      },
    });
    const isBJ = isBlackjack(gameId);

    const openSeats = isBJ
      ? table.history.filter((seat) => !seat.Taken).length - 1
      : null;

    const occupiedSeats = isBJ ? MAX_SEATS - openSeats : null;

    return analytics.push({
      event: LOBBY_PLAY,
      table_id: tableId,
      dealer_id: table?.dealer_id,
      open_seats: openSeats,
      occupied_seats: occupiedSeats,
      direct_seat: !!seatIndex,
      active_players: table.playersCount,
      new: table.new,
      page_url: pageURL,
      recent:
          recentSelector(state$.value).findIndex(
            (value) => value === tableId,
          ) > -1,
      favorite:
          favoriteSelector(state$.value).findIndex(
            (value) => value === tableId,
          ) > -1,
    });
  }),
);

// Send relevant initial data about the app and user (that GA doesn't send by default)
const lobbyInitialData = (action$, state$) => action$.pipe(
  ofType(userSlice.request, userSlice.failure, socket.failure, config.failure, app.start),
  timestamp(),
  mergeMap(({ value: action, timestamp: initialTimestamp }) => {
    if (action.type === app.start.type) {
      if (window.location.pathname.includes('category')) {
        return of({ action, timestamp: initialTimestamp });
      }

      return interval(500).pipe(
        filter(() => window.location.pathname.includes('category')),
        first(),
        map(() => ({ action, timestamp: initialTimestamp }))
      );
    }
    return of({ action, timestamp: initialTimestamp });
  }),
  map(({ action, timestamp: initialTimestamp }) => {
    const isInitialRequest = action.type === userSlice.request.type;
    const isSuccess = action.type === app.start.type;
    const isFailedRequest = [ socket.failure.type, userSlice.failure.type, config.failure.type ]
      .includes(action.type);

    const category = categoriesSelector(state$.value).find((cat) => cat.id === +window.location.pathname
      .split('category/')[1]);

    const event = {
      event: LOBBY_INITIAL_DATA,
      loadingTime: isSuccess ? getDiffTimeAsADouble(userRequestTimestampSelector(state$.value), initialTimestamp) : 0,
      loadingStatus: isInitialRequest
        ? LOADING_STATUS_INITIALIZATION
        : isFailedRequest
          ? LOADING_STATUS_ERROR
          : LOADING_STATUS_SUCCESS,
      category_name: category?.id === 0 ? 'My Lobby' : (category?.name || category?.id),
      ...(isFailedRequest && { error: appErrorSelector(state$.value) }),
    };
    return analytics.push(event);
  })
);

const getOutgoingCallsConfigEpic = (action$) => action$.pipe(
  ofType(analytics.requestConfig),
  switchMap(() => from(fetchOutgoingCalls())),
  tap(({ result }) => {
    if (!result.outgoingCalls) return;
    import('@ezugi/analytics');
    const {
      setEnabled,
      clearBasePayload,
      setDatadogEnabled,
    } = analyticsStore.getState();
    setEnabled(result.outgoingCalls);
    clearBasePayload();
    setDatadogEnabled(result.dataDogEnabled);
    if (!result.dataDogEnabled) {
      datadogLogs.logger.setHandler('silent');
      datadogRum.stopSession();
    }
  }),
  flatMap(({ outgoingCalls }) => concat(
    of(config.set({ outgoingCalls })),
    of(userSlice.request())
  )),
  catchError(() => {
    // We add analytics if request fails
    import('@ezugi/analytics');
    return of(userSlice.request());
  })
);

const orientationChangeMobileEpic = () => fromEvent(window.matchMedia(mq.portrait), 'change').pipe(
  filter(() => DeviceInfo.isHandheld),
  map((event) => {
    const sharedProperties = {
      event: CHANGE_ORIENTATION,
      frame: getFrame(),
      resolution: `${window.innerWidth},${window.innerHeight}`,
    };

    if (event.matches) {
      return analytics.push({
        ...sharedProperties,
        oldpos: ORIENTATION.landscape,
        newpos: ORIENTATION.portrait,
      });
    }
    return analytics.push({
      ...sharedProperties,
      oldpos: ORIENTATION.portrait,
      newpos: ORIENTATION.landscape,
    });
  }),
);

const playerHistoryRequestRoundEpic = (action$) => action$.pipe(
  filter(history.requestDay.match),
  map(() => analytics.push({
    event: GAME_MENU_PLAYER_HISTORY_DAILY,
    origin: 'lobby',
  }))
);

export default combineEpics(
  setUserIdEpic,
  analyticsPushEpic,
  lobbyCategoryClick,
  lobbyFavorite,
  lobbyPlay,
  lobbyInitialData,
  getOutgoingCallsConfigEpic,
  orientationChangeMobileEpic,
  playerHistoryRequestRoundEpic
);
